/**
 * use-auth.ts — STANDAR Indopoin pattern dari RUNBOOK/04_SNIPPETS/frontend/use-auth.ts
 *
 * Auth state + bootstrap. Pakai React Query untuk fetch /api/me.
 */

import { useEffect, useState, createContext, useContext } from 'react';
import { getStoredToken, extractTokenFromURL, redirectToLogout } from '../utils/token-storage';
import { api } from '../lib/api';

export interface User {
  id: string;
  username?: string;
  email?: string;
  role?: string;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  logout: () => void;
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuthProvider() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUser = async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get<{ user: User }>('/api/me');
      setUser(res.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 1. Extract token dari URL (kalau ada)
    extractTokenFromURL();
    // 2. Load user (kalau ada token)
    loadUser();
  }, []);

  return {
    user,
    loading,
    logout: () => redirectToLogout(),
    refresh: loadUser,
  };
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside <AuthContext.Provider>');
  return ctx;
}